import BaseNode from "~/components/Playground/Node/BaseNode";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import {
  BlockStep,
  InputStep,
  Outcome,
  State,
  hasReceivedInput,
  isInputDisabled,
} from "~/lib/pipeline";
import { Node, NodeProps } from "@xyflow/react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { forwardRef, memo } from "react";
import { isInputJobUnblocked, InputJob } from "~/components/build/Show/lib/types";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import UserAvatar, { UserAvatarSize } from "~/components/shared/UserAvatar";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { twMerge } from "tailwind-merge";
import Emojify from "~/components/shared/Emojify";
import { HoverCardContentProps } from "@radix-ui/react-hover-card";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { useQuery } from "@tanstack/react-query";

interface InputStepHoverCardProps extends HoverCardContentProps {
  step: InputStep | BlockStep;
}

const useInputJob = (step: InputStep | BlockStep): InputJob | null => {
  const { build } = useBuild();
  const eagerLoadedJobs = build.jobs.filter((job) => job.stepUuid === step.uuid);

  const { data: lazilyLoadedJobs } = useQuery(
    stepJobsQueryOptions(build, step.uuid, { enabled: Features.BuildSidebar }),
  );

  const jobs = Features.BuildSidebar ? lazilyLoadedJobs : eagerLoadedJobs;

  return (jobs?.[jobs.length - 1] as InputJob) ?? null;
};

/**
 * Deferred hover content that fetches job data only when mounted.
 * Since this is rendered inside HoverCard.Portal (which lazy-mounts on hover),
 * the fetch only fires when the user actually hovers over the node.
 */
const InputStepHoverContent = ({ step }: { step: InputStep | BlockStep }) => {
  const job = useInputJob(step);

  const unblockedJob = job && isInputJobUnblocked(job) ? job : null;
  const actor =
    step.actor ||
    (unblockedJob
      ? { name: unblockedJob.unblockedBy.name, avatarUrl: unblockedJob.unblockedBy.avatarUrl }
      : null);

  if (!actor) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex w-full flex-row items-center gap-2">
        <UserAvatar
          className="h-10 w-10"
          user={{
            name: actor.name,
            avatar: { url: actor.avatarUrl },
          }}
        />
        <div className="flex flex-1 flex-col flex-nowrap">
          <div className="font-medium">{actor.name}</div>
          {unblockedJob && (
            <div className="dark-gray">
              Unblocked <FriendlyTime value={unblockedJob.unblockedAt} capitalized={false} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const InputStepHoverCard = forwardRef<HTMLDivElement, InputStepHoverCardProps>(
  ({ step, ...props }, ref) => {
    const { missingDependencies } = useStepDependencies(step.uuid);

    return (
      <HoverCard.Content
        ref={ref}
        state={step.state}
        outcome={step.outcome}
        missingDependencies={missingDependencies}
        {...props}
      >
        <InputStepHoverContent step={step} />
      </HoverCard.Content>
    );
  },
);

interface UnblockButtonProps {
  step: InputStep | BlockStep;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const UnblockButton = forwardRef<HTMLAnchorElement, UnblockButtonProps>(
  ({ step, children, className, ...props }, ref) => {
    const { build } = useBuild({ strict: false });
    const job = useInputJob(step);

    // No build context in the standalone playground or admin graph
    if (!build) {
      // eslint-disable-next-line react/jsx-no-useless-fragment -- forwardRef requires ReactElement, not ReactNode
      return <>{children}</>;
    }

    // Progressive: construct URL from step statistics (populated by BuildPage controllers)
    // Legacy: use job.unblockDialogPath (from build.jobs, already loaded)
    const latestJobId = step.statistics?.latestJobId;
    const unblockDialogPath = latestJobId
      ? `/organizations/${build.account.slug}/pipelines/${build.project.slug}/builds/${build.number}/jobs/${latestJobId}/unblock_dialog`
      : job?.unblockDialogPath;

    // Disable unblocking when the step can't accept input (canceled, skipped/broken, or waiting on failed deps)
    const disabled =
      step.state === State.Canceled ||
      step.state === State.Ignored ||
      step.state === State.WaitingForDependencies;

    return (
      <a
        {...props}
        ref={ref}
        href={unblockDialogPath}
        aria-disabled={disabled || undefined}
        data-turbo={true}
        data-turbo-frame="unblock_dialog"
        className={twMerge(
          "text-inherit no-underline hover:text-inherit focus:text-inherit",
          className,
        )}
      >
        {children}
      </a>
    );
  },
);

type InputStepNode = Node<InputStep, "input">;

export const InputStepUser = ({
  step,
  size = "REGULAR",
}: {
  step: InputStep | BlockStep;
  size?: UserAvatarSize;
}) => {
  const job = useInputJob(step);

  // Progressive: step.actor is populated by BuildPage controllers
  // Legacy: fall back to job.unblockedBy from build.jobs (already loaded)
  const actor =
    step.actor ||
    (job?.unblockedBy
      ? { name: job.unblockedBy.name, avatarUrl: job.unblockedBy.avatarUrl || "" }
      : null);

  if (!actor) {
    return null;
  }

  return (
    <UserAvatar
      user={{
        name: actor.name,
        avatar: {
          url: actor.avatarUrl,
        },
      }}
      size={size}
    />
  );
};

function InputStepNode(props: NodeProps<InputStepNode>) {
  const { build } = useBuild({ strict: false });
  const { missingDependencies } = useStepDependencies(props.id);

  const node = (
    <div>
      <BaseNode
        {...props}
        label={<Emojify text={props.data.label || "Continue"} />}
        icon={
          <span className="ml-1 flex">
            {build && hasReceivedInput(props.data) ? (
              <>
                <InputStepUser step={props.data} />

                <div className="flex shrink-0 items-center justify-center self-stretch">
                  <StateIcon
                    state={State.Finished}
                    outcome={Outcome.Passed}
                    missingDependencies={missingDependencies}
                  />
                </div>
              </>
            ) : (
              <span
                className={twMerge(
                  classNames(
                    "absolute -bottom-[1px] -right-[1px] -top-[1px] flex w-10 items-center justify-center rounded-r-lg bg-purple-600",
                    {
                      "bg-slate-500": isInputDisabled(props.data),
                    },
                  ),
                )}
              >
                <Icon icon="arrow-right" className="h-5 w-5 text-white" />
              </span>
            )}
          </span>
        }
        disabled={isInputDisabled(props.data)}
        className={classNames({
          "opacity-100": props.data.state !== State.Ignored,
          "border-4 border-purple-600 pr-10":
            !hasReceivedInput(props.data) && !isInputDisabled(props.data),
          "border-2 pr-10 text-slate-500": isInputDisabled(props.data),
        })}
      />
    </div>
  );

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {build && hasReceivedInput(props.data) && (
          <InputStepHoverCard
            step={props.data}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          />
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        {!build || hasReceivedInput(props.data) || isInputDisabled(props.data) ? (
          node
        ) : (
          <UnblockButton step={props.data}>{node}</UnblockButton>
        )}
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(InputStepNode);
